import { useAuth } from "@stores/auth-store";
import {
  COMPANY_PRINT_DELETE_ENDPOINT,
  COMPANY_PRINT_GET_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { parseCookies } from "nookies";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card";
import { DictType } from "@pma/types";
import { Button } from "@components/common/button";

import { toast } from "@hooks/use-toast";
import { Printer, X } from "lucide-react";
import PmsLoader from "@components/common/loader";
import useTranslator from "@hooks/use-translator";
import { cn } from "@pma/utils/helpers";
import { Badge } from "../common/badge";
import AddPrinter from "./add-printer";
import { format } from "date-fns";
import { he } from "date-fns/locale";

const PrinterSettings = () => {
  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();

  const orgId = authConfig?.last_organization_id;

  const isHebrew = locale === "he";

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const getPrintServices = async () => {
    const response = await axios.get(
      SERVER_URL + COMPANY_PRINT_GET_ENDPOINT + `?organization_id=${orgId}`,

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["print-service", orgId],
    queryFn: getPrintServices,
    enabled: !!orgId,
  });

  const deletePrintService = async (printer: DictType) => {
    const response = await axios.delete(
      SERVER_URL + COMPANY_PRINT_DELETE_ENDPOINT,
      {
        params: { print_id: printer.id, organization_id: orgId },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const deletePrintServiceMutation = useMutation({
    mutationKey: ["delete-print-service"],
    mutationFn: (printer: DictType) => deletePrintService(printer),
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Print service has been deleted"),
        variant: "success",
      });

      refetch();
    },
  });

  return (
    <Card className="">
      <CardHeader className="flex flex-row items-center justify-between w-full border-b">
        <CardTitle className="flex gap-2">
          <Printer size={18} />
          {translate("Printer Settings")}
        </CardTitle>

          <AddPrinter refetch={refetch} />

      </CardHeader>
      <CardContent className="flex flex-col gap-4 text-sm p-6 relative min-h-fit">
        {isLoading && <PmsLoader />}
        {!isLoading && data?.length < 1 && (
          <div className="flex items-center justify-center h-[20vh]">
            {translate("No printers found")}
          </div>
        )}

        <div className="grid gap-4">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {data?.map((printer: DictType) => (
              <Card key={printer?.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {printer?.name}
                    <Printer size={18} />
                  </CardTitle>
                  <CardDescription>{printer.email}</CardDescription>
                </CardHeader>
                <CardContent>
                  <h4 className="font-semibold text-xs">
                    {translate("Formats")}
                  </h4>
                  <div className="grid">
                    {printer?.formats?.map((format: string, index: number) => (
                      <Badge className="" key={index}>
                        {format}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between text-xs">
                  <Button
                    size={"sm"}
                    className="h-6 text-xs text-destructive/50 p-0.5"
                    variant={"ghost"}
                    rightSection={<X size={14} />}
                    onClick={() => deletePrintServiceMutation.mutate(printer)}
                  >
                    {translate("Remove")}
                  </Button>
                  <small className="flex gap-0.5">
                    {translate("Added")}:
                    {format(printer?.created_at, "dd MMM yyy", {
                      locale: isHebrew ? he : undefined,
                    })}
                  </small>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PrinterSettings;
